import { LegalLayout, LegalSection, useIsJa } from '@/components/layout/LegalLayout';
import { Link } from 'react-router-dom';
import { ReactNode } from 'react';

interface Variant {
  h: string;
  body: ReactNode;
}

interface Section {
  id: string;
  en: Variant;
  ja: Variant;
}

const SECTIONS: Section[] = [
  {
    id: 'service',
    en: {
      h: '1. The Service',
      body: (
        <p>
          CadencEZ provides cloud-based budgeting, forecasting, variance analysis, consolidation, and
          reporting tools, including AI-assisted features. We may update or improve the Service over time;
          we will not materially reduce core functionality of a paid plan during a paid term without notice.
        </p>
      ),
    },
    ja: {
      h: '1. 本サービス',
      body: (
        <p>
          CadencEZは、AI支援機能を含む、クラウドベースの予算策定、予測、差異分析、連結、およびレポート作成のためのツールを提供します。
          当社は本サービスを随時更新または改善することがあります。当社は、有料期間中の有料プランの中核機能を、通知なく重大な程度に縮小することはありません。
        </p>
      ),
    },
  },
  {
    id: 'accounts',
    en: {
      h: '2. Accounts & eligibility',
      body: (
        <p>
          You must provide accurate account information, keep credentials secure, and are responsible for
          activity under your account. You must be able to form a binding contract and use the Service only
          for lawful business purposes.
        </p>
      ),
    },
    ja: {
      h: '2. アカウントおよび利用資格',
      body: (
        <p>
          お客様は、正確なアカウント情報を提供し、認証情報を安全に管理する必要があり、お客様のアカウントの下で行われる活動について責任を負います。
          お客様は、拘束力のある契約を締結する能力を有していなければならず、本サービスを適法な事業目的のためにのみ使用するものとします。
        </p>
      ),
    },
  },
  {
    id: 'fees',
    en: {
      h: '3. Subscriptions, fees & taxes',
      body: (
        <p>
          Paid plans are billed per seat on the interval you select (monthly or yearly) via our payment
          processor, Stripe. Fees are stated exclusive of taxes; applicable VAT, GST, consumption tax (JCT),
          or sales tax will be added where required. Except where required by law, fees are non-refundable.
          We may change pricing for future terms with notice.
        </p>
      ),
    },
    ja: {
      h: '3. サブスクリプション、料金および税金',
      body: (
        <p>
          有料プランは、お客様が選択した期間（月次または年次）に応じ、シートごとに、当社の決済処理事業者であるStripeを通じて請求されます。
          料金は税抜きで表示されます。該当する付加価値税（VAT）、物品サービス税（GST）、消費税（JCT）、または売上税が、必要な場合に追加されます。
          法令により義務付けられる場合を除き、料金は返金不可です。当社は、通知のうえ、将来の契約期間の料金を変更することがあります。
        </p>
      ),
    },
  },
  {
    id: 'data',
    en: {
      h: '4. Customer data & ownership',
      body: (
        <p>
          As between the parties, you own the data you upload or connect ("Customer Data"). You grant us a
          limited license to host and process Customer Data solely to provide the Service. Our handling of
          personal data within Customer Data is governed by our <Link to="/dpa">DPA</Link> and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      ),
    },
    ja: {
      h: '4. 顧客データおよび所有権',
      body: (
        <p>
          両当事者間において、お客様がアップロードまたは連携するデータ（以下「顧客データ」といいます。）はお客様に帰属します。
          お客様は、本サービスを提供する目的のためにのみ、当社が顧客データをホスティングおよび処理するための限定的なライセンスを当社に付与します。
          顧客データに含まれる個人データの取扱いは、当社の<Link to="/dpa">DPA</Link>および
          <Link to="/privacy">プライバシーポリシー</Link>に従います。
        </p>
      ),
    },
  },
  {
    id: 'ai',
    en: {
      h: '5. AI-assisted features',
      body: (
        <p>
          The Service includes AI features that generate analysis, narratives, and suggestions, with
          citations to your underlying data where applicable. AI output may contain errors and is provided
          to assist — not replace — professional judgment. You are responsible for reviewing AI output
          before relying on it for financial, accounting, tax, or business decisions. CadencEZ is not your
          accountant, auditor, or financial adviser.
        </p>
      ),
    },
    ja: {
      h: '5. AI支援機能',
      body: (
        <p>
          本サービスには、分析、説明文、および提案を生成するAI機能が含まれ、該当する場合には、根拠となるお客様のデータへの出典を付します。
          AIの出力には誤りが含まれる可能性があり、専門的判断を置き換えるものではなく、これを支援するために提供されます。
          お客様は、財務、会計、税務、または事業上の意思決定のためにAIの出力に依拠する前に、その出力を確認する責任を負います。
          CadencEZは、お客様の会計士、監査人、または財務アドバイザーではありません。
        </p>
      ),
    },
  },
  {
    id: 'acceptable-use',
    en: {
      h: '6. Acceptable use',
      body: (
        <p>
          You agree not to misuse the Service, including: reverse engineering, circumventing security or
          usage limits, uploading unlawful or infringing content, attempting to access other customers'
          data, or using the Service to build a competing product.
        </p>
      ),
    },
    ja: {
      h: '6. 許容される利用',
      body: (
        <p>
          お客様は、本サービスを濫用しないことに同意します。これには、リバースエンジニアリング、セキュリティもしくは利用上限の回避、
          違法もしくは権利を侵害するコンテンツのアップロード、他の顧客のデータへのアクセスの試み、または競合製品の構築のための本サービスの利用が含まれます。
        </p>
      ),
    },
  },
  {
    id: 'integrations',
    en: {
      h: '7. Third-party integrations',
      body: (
        <p>
          The Service can connect to third-party tools (e.g. freee, Money Forward, QuickBooks, Xero,
          NetSuite). Your use of those tools is governed by their own terms; we are not responsible for
          third-party services.
        </p>
      ),
    },
    ja: {
      h: '7. 第三者との連携',
      body: (
        <p>
          本サービスは、第三者のツール（例：freee、Money Forward、QuickBooks、Xero、NetSuite）と連携することができます。
          これらのツールのお客様による利用は、それぞれのツールの規約に従います。当社は、第三者のサービスについて責任を負いません。
        </p>
      ),
    },
  },
  {
    id: 'warranties',
    en: {
      h: '8. Warranties & disclaimers',
      body: (
        <p>
          We provide the Service with reasonable skill and care. Except as expressly stated, the Service is
          provided "as is" without warranties of any kind, to the maximum extent permitted by law. We do
          not warrant that the Service will be uninterrupted or error-free.
        </p>
      ),
    },
    ja: {
      h: '8. 保証および免責',
      body: (
        <p>
          当社は、合理的な技能および注意をもって本サービスを提供します。明示的に定める場合を除き、本サービスは、法令により認められる最大限の範囲において、
          いかなる種類の保証も伴わない「現状有姿」で提供されます。当社は、本サービスが中断されないこと、または誤りがないことを保証しません。
        </p>
      ),
    },
  },
  {
    id: 'liability',
    en: {
      h: '9. Limitation of liability',
      body: (
        <p>
          To the maximum extent permitted by law, neither party is liable for indirect, incidental,
          special, or consequential damages, or lost profits or data. Our total aggregate liability arising
          out of these Terms is limited to the fees you paid for the Service in the twelve months before the
          event giving rise to the claim. Nothing limits liability that cannot be limited by law.
        </p>
      ),
    },
    ja: {
      h: '9. 責任の制限',
      body: (
        <p>
          法令により認められる最大限の範囲において、いずれの当事者も、間接損害、付随的損害、特別損害もしくは結果的損害、または逸失利益もしくはデータの損失について責任を負いません。
          本規約に起因する当社の累積的な責任の総額は、請求の原因となる事象の発生前12か月間にお客様が本サービスについて支払った料金を上限とします。
          法令により制限することができない責任については、本条のいかなる定めもこれを制限しません。
        </p>
      ),
    },
  },
  {
    id: 'termination',
    en: {
      h: '10. Term & termination',
      body: (
        <p>
          These Terms apply while you use the Service. Either party may terminate for material breach not
          cured within 30 days. On termination, your right to use the Service ends; you may export Customer
          Data for a limited period, after which we may delete it per our retention practices.
        </p>
      ),
    },
    ja: {
      h: '10. 契約期間および解除',
      body: (
        <p>
          本規約は、お客様が本サービスを利用する間、適用されます。いずれの当事者も、30日以内に是正されない重大な違反を理由として、本規約を解除することができます。
          解除時には、お客様の本サービスの利用権は終了します。お客様は、限定された期間、顧客データをエクスポートすることができ、その後、当社は当社の保持方針に従ってこれを削除することがあります。
        </p>
      ),
    },
  },
  {
    id: 'governing-law',
    en: {
      h: '11. Governing law & jurisdiction',
      body: (
        <p>
          These Terms, and any dispute or claim arising out of or in connection with them or their subject
          matter, are governed by the laws of the State of Delaware, USA, without regard to its
          conflict-of-laws rules. Subject to the dispute-resolution process below, the parties submit to the
          exclusive jurisdiction of the state and federal courts located in Delaware, USA.
        </p>
      ),
    },
    ja: {
      h: '11. 準拠法および管轄裁判所',
      body: (
        <p>
          本規約、ならびに本規約もしくはその対象事項に起因または関連して生じるあらゆる紛争または請求は、抵触法の規則にかかわらず、米国デラウェア州の法律に準拠します。
          下記の紛争解決手続に従うことを条件として、両当事者は、米国デラウェア州に所在する州裁判所および連邦裁判所の専属的管轄に服することに合意します。
        </p>
      ),
    },
  },
  {
    id: 'dispute-resolution',
    en: {
      h: '12. Dispute resolution',
      body: (
        <p>
          Before commencing litigation, the parties will first attempt to resolve any dispute arising out of
          or relating to these Terms in good faith through informal negotiation. A party must give the other
          written notice describing the dispute; the parties will then have 30 days from that notice to reach
          a resolution. If the dispute is not resolved within that period, either party may pursue litigation,
          and the venue for any such proceedings will be the state and federal courts located in Delaware, USA.
        </p>
      ),
    },
    ja: {
      h: '12. 紛争解決',
      body: (
        <p>
          訴訟を開始する前に、両当事者は、まず、本規約に起因または関連して生じる紛争を、誠実な非公式の協議を通じて解決するよう努めます。
          一方の当事者は、当該紛争を記載した書面による通知を相手方に行うものとし、両当事者は、その通知から30日間、解決に至るよう努めます。
          当該期間内に紛争が解決しない場合、いずれの当事者も訴訟を提起することができ、かかる手続の管轄は、米国デラウェア州に所在する州裁判所および連邦裁判所とします。
        </p>
      ),
    },
  },
  {
    id: 'indemnification',
    en: {
      h: '13. Indemnification',
      body: (
        <p>
          You will indemnify, defend, and hold harmless CadencEZ and its affiliates, officers, and employees
          from and against any third-party claims, damages, liabilities, costs, and expenses (including
          reasonable legal fees) arising out of or relating to: (a) Customer Data; (b) your use of the
          Service in breach of these Terms or applicable law; or (c) your infringement or misappropriation of
          any third-party rights.
        </p>
      ),
    },
    ja: {
      h: '13. 補償',
      body: (
        <p>
          お客様は、以下に起因または関連して生じるあらゆる第三者からの請求、損害、責任、費用および経費（合理的な弁護士費用を含みます。）から、
          CadencEZならびにその関係会社、役員および従業員を補償し、防御し、免責するものとします。すなわち、(a) 顧客データ、
          (b) 本規約または適用される法令に違反するお客様による本サービスの利用、または (c) お客様による第三者の権利の侵害もしくは不正利用です。
        </p>
      ),
    },
  },
  {
    id: 'confidentiality',
    en: {
      h: '14. Confidentiality',
      body: (
        <p>
          Each party (as receiving party) will protect the other party's non-public information disclosed in
          connection with these Terms with at least the same degree of care it uses for its own confidential
          information, and will use it only to perform under these Terms. These obligations do not apply to
          information that: (a) is or becomes public through no fault of the receiving party; (b) was
          independently developed without use of the disclosing party's confidential information; (c) was
          lawfully received from a third party without a duty of confidentiality; or (d) is required to be
          disclosed by law, provided the receiving party gives reasonable notice where permitted.
        </p>
      ),
    },
    ja: {
      h: '14. 秘密保持',
      body: (
        <p>
          各当事者（受領当事者として）は、本規約に関連して開示された相手方の非公開情報を、自己の秘密情報に対して払うのと少なくとも同程度の注意をもって保護し、
          本規約を履行する目的のためにのみこれを使用します。これらの義務は、次の情報には適用されません。すなわち、
          (a) 受領当事者の責めによらずに公知となった、または公知となる情報、(b) 開示当事者の秘密情報を使用することなく独自に開発された情報、
          (c) 秘密保持義務を負うことなく第三者から適法に受領した情報、または (d) 法令により開示が義務付けられる情報（ただし、認められる場合、受領当事者は合理的な通知を行うものとします。）です。
        </p>
      ),
    },
  },
  {
    id: 'force-majeure',
    en: {
      h: '15. Force majeure',
      body: (
        <p>
          Neither party will be liable for any delay or failure to perform its obligations (other than
          payment obligations) to the extent caused by events beyond its reasonable control, including acts
          of God, natural disasters, war, terrorism, civil unrest, labor disputes, governmental action,
          internet or utility failures, and failures of third-party providers.
        </p>
      ),
    },
    ja: {
      h: '15. 不可抗力',
      body: (
        <p>
          いずれの当事者も、天災地変、自然災害、戦争、テロ行為、内乱、労働争議、政府の措置、インターネットもしくは公益サービスの障害、
          および第三者プロバイダーの障害を含む、その合理的な支配を超える事象に起因する限りにおいて、その義務（支払義務を除きます。）の履行の遅延または不履行について責任を負いません。
        </p>
      ),
    },
  },
  {
    id: 'general',
    en: {
      h: '16. General',
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Entire agreement.</strong> These Terms, together with the DPA and Privacy Policy, are the
            entire agreement between the parties regarding the Service and supersede any prior agreements on
            that subject.
          </li>
          <li>
            <strong>Severability.</strong> If any provision is held unenforceable, the remaining provisions
            remain in full force and effect.
          </li>
          <li>
            <strong>No waiver.</strong> A party's failure to enforce any provision is not a waiver of its
            right to do so later.
          </li>
          <li>
            <strong>Assignment.</strong> CadencEZ may assign these Terms in connection with a merger,
            acquisition, or sale of assets, or to an affiliate; you may not assign these Terms without our
            prior written consent.
          </li>
          <li>
            <strong>Notices.</strong> Legal notices to CadencEZ may be sent to{' '}
            <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>; notices to you may be sent to your
            account administrators or shown in the Service.
          </li>
          <li>
            <strong>No third-party beneficiaries.</strong> These Terms do not create any rights for anyone
            other than the parties.
          </li>
          <li>
            <strong>Survival.</strong> Provisions that by their nature should survive termination (including
            fees accrued, confidentiality, indemnification, liability limits, and governing law) survive.
          </li>
          <li>
            <strong>Independent contractors.</strong> The parties are independent contractors; these Terms
            create no partnership, joint venture, or agency relationship.
          </li>
          <li>
            <strong>Export & sanctions.</strong> Each party will comply with applicable export-control and
            sanctions laws, and you represent that you are not located in, or acting on behalf of, an
            embargoed or restricted party.
          </li>
        </ul>
      ),
    },
    ja: {
      h: '16. 一般条項',
      body: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>完全合意。</strong>{' '}
            本規約は、DPAおよびプライバシーポリシーと併せて、本サービスに関する両当事者間の完全な合意を構成し、当該事項に関する従前のあらゆる合意に優先します。
          </li>
          <li>
            <strong>分離可能性。</strong>{' '}
            いずれかの条項が執行不能と判断された場合であっても、残りの条項は引き続き完全な効力を有します。
          </li>
          <li>
            <strong>権利不放棄。</strong>{' '}
            一方の当事者がいずれかの条項を行使しなかったとしても、後にこれを行使する権利を放棄したものとはみなされません。
          </li>
          <li>
            <strong>譲渡。</strong>{' '}
            CadencEZは、合併、買収もしくは資産譲渡に関連して、または関係会社に対して、本規約を譲渡することができます。お客様は、当社の事前の書面による同意なく本規約を譲渡することはできません。
          </li>
          <li>
            <strong>通知。</strong> CadencEZに対する法的通知は{' '}
            <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>{' '}
            宛てに送付することができます。お客様に対する通知は、お客様のアカウント管理者宛てに送付し、または本サービス内に表示することができます。
          </li>
          <li>
            <strong>第三者の受益者の不存在。</strong>{' '}
            本規約は、両当事者以外のいかなる者に対しても権利を生じさせるものではありません。
          </li>
          <li>
            <strong>存続。</strong>{' '}
            その性質上、契約終了後も存続すべき条項（発生した料金、秘密保持、補償、責任の制限および準拠法を含みます。）は、終了後も存続します。
          </li>
          <li>
            <strong>独立当事者。</strong>{' '}
            両当事者は独立した契約当事者であり、本規約は、パートナーシップ、ジョイントベンチャーまたは代理関係を生じさせるものではありません。
          </li>
          <li>
            <strong>輸出および制裁措置の遵守。</strong>{' '}
            各当事者は、適用される輸出管理法令および制裁法令を遵守するものとし、お客様は、自らが禁輸対象もしくは制限対象の当事者に所在せず、またはこれらの者を代理して行動していないことを表明します。
          </li>
        </ul>
      ),
    },
  },
  {
    id: 'changes-contact',
    en: {
      h: '17. Changes & contact',
      body: (
        <p>
          We may update these Terms; material changes will be notified to account administrators and the
          "Last updated" date revised. Questions:{' '}
          <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>.
        </p>
      ),
    },
    ja: {
      h: '17. 変更および連絡先',
      body: (
        <p>
          当社は本規約を更新することがあります。重要な変更については、アカウント管理者に通知し、「最終更新日」を改訂します。お問い合わせ：{' '}
          <a href="mailto:legal@cadencez.com">legal@cadencez.com</a>。
        </p>
      ),
    },
  },
];

export function TermsPage() {
  const isJa = useIsJa();
  return (
    <LegalLayout
      title={isJa ? '利用規約' : 'Terms of Service'}
      updated="2026-06-03"
      metaTitle={isJa ? '利用規約 — CadencEZ' : 'Terms of Service — CadencEZ'}
      metaDescription={
        isJa
          ? 'CadencEZの財務計画・分析サービスのご利用を規律する規約。サブスクリプション、許容される利用、準拠法、紛争解決、補償、秘密保持、責任の制限、解除を含みます。'
          : 'The terms governing your use of the CadencEZ financial planning & analysis service, including subscriptions, acceptable use, governing law, dispute resolution, indemnification, confidentiality, liability, and termination.'
      }
      path="/terms"
    >
      {isJa ? (
        <p>
          本利用規約（以下「本規約」といいます。）は、CadencEZ Inc.（米国デラウェア州設立。以下「CadencEZ」または「当社」といいます。）と、
          本規約に同意する法人または個人（以下「お客様」といいます。）との間の、CadencEZのウェブサイトおよび財務計画・分析サービス（以下「本サービス」といいます。）の
          利用を規律する拘束力のある契約です。アカウントを作成し、または本サービスを利用することにより、お客様は本規約に同意するものとします。
        </p>
      ) : (
        <p>
          These Terms of Service ("Terms") are a binding agreement between CadencEZ Inc. ("CadencEZ", "we"),
          a company incorporated in Delaware, USA, and the entity or person agreeing to them ("you",
          "Customer") governing use of the CadencEZ website and financial planning &amp; analysis service
          (the "Service"). By creating an account or using the Service, you accept these Terms.
        </p>
      )}

      {SECTIONS.map((s) => {
        const c = isJa ? s.ja : s.en;
        return (
          <LegalSection key={s.id} heading={c.h}>
            {c.body}
          </LegalSection>
        );
      })}
    </LegalLayout>
  );
}
